import React from "react"
import {Payment} from "../../resources/payment"
import {Meta, Resource} from "../../resources/common"
import {AllowedPaymentColumns, PaymentColumns} from "./Payments.types"
import {DataTable, DataTableBody, DataTableCell, DataTableHead, DataTableRow} from "../DataTable/DataTable"
import PagingNavBar from "../PagingNavBar/PagingNavBar"
import Drawer from "../Drawer/Drawer"
import {useNavigate} from "react-router-dom"
import {useLocation} from "react-router-dom"
import {useAccessToken} from "../../services/auth"
import {useIsUnitUser} from "../../services/auth"
import {useState, useRef, useEffect} from "react"
import {PaymentComponent} from "./Payment"
import {isWirePayment} from "../../resources/payment"
import {isSandboxOrLocalEnv} from "../../utilities/environment"
import {isProdEnv} from "../../utilities/environment"
import {upperCase} from "lodash"
import PaymentRow from "./PaymentRow"
import classNames from "classnames"
import {paymentTabQueryStateName} from "../../pages/Payments/Payment"
import {PaymentTabConstants} from "../../pages/Payments/PaymentTabs"

interface PaymentsTableProps {
	payments: Payment[]
	selectedPaymentId: string
	setSelectedPaymentId: (id: string) => void
	hasResults: boolean
	hasPrev: boolean
	hasNext: boolean
	prev: () => void
	next: () => void
	isUsingPaging: boolean
	include?: Resource[]
	includedColumns: AllowedPaymentColumns[]
	meta?: Meta
	refresh: () => void
	fullHeight?: boolean
	showTabsOnHeader?: boolean
	sortFunction: () => void
	sortBy: string
	disableDrawer?: boolean
	hideMinHeight?: boolean
	disabled?: boolean
	token?: string
}

export function PaymentsTable({
	payments,
	selectedPaymentId,
	setSelectedPaymentId,
	fullHeight,
	showTabsOnHeader,
	includedColumns,
	hasNext,
	hasPrev,
	hasResults,
	isUsingPaging,
	include,
	refresh,
	meta,
	prev,
	next,
	sortFunction,
	disableDrawer = false,
	sortBy,
	hideMinHeight,
	disabled,
	token,
}: PaymentsTableProps) {
	const navigate = useNavigate()
	const location = useLocation()
	const accessToken = token ?? useAccessToken()
	const isUnitUser = useIsUnitUser()
	const noContent = payments.length === 0
	const [isDrawerOpen, setIsDrawerOpen] = useState(!!selectedPaymentId)
	const ref = useRef(null)
	const isRedirectedFromAssignments = location.pathname.includes("/assignments")

	const toggleDrawer = (reopenWithId = false) => {
		setIsDrawerOpen((prevState) => !prevState)
		if (reopenWithId) {
			setTimeout(() => {
				setIsDrawerOpen(true)
			}, 200)
		}
	}
	useEffect(() => {
		if (!selectedPaymentId) {
			setIsDrawerOpen(false)
		}
	}, [selectedPaymentId])

	return (
		<div ref={ref} className={"payments-table"}>
			<DataTable
				isEmpty={noContent}
				fullHeight={fullHeight}
				stickyAction={fullHeight}
				stickyActionWithTabsOnHeader={fullHeight && showTabsOnHeader}
				noContentText={"No payments found"}
				hideMinHeight={hideMinHeight}
				disabled={disabled}
			>
				<DataTableHead className={classNames(!fullHeight && "no-sticky")}>
					<DataTableRow>
						{Object.entries(includedColumns).map((column) => {
							if (column[1] === PaymentColumns.createdAt) {
								return (
									<DataTableCell
										clickable
										onClick={() => sortFunction()}
										key={column[1]}
										sortable
										sortApplied={sortBy === "createdAt"}
									>
										{column[1]}
									</DataTableCell>
								)
							}

							const columnHeader = column[1] === PaymentColumns.id ? upperCase(column[1]) : column[1]

							return <DataTableCell key={column[0]}>{columnHeader}</DataTableCell>
						})}
					</DataTableRow>
				</DataTableHead>
				<DataTableBody>
					{payments.map((payment) => (
						<PaymentRow
							payment={payment}
							key={payment.id}
							include={include}
							onClick={(e) => {
								e.preventDefault()
								e.stopPropagation()
								const sandboxAndLocalOrgIds = [
									"3475",
									"2654",
									"5297",
									"5314",
									"5326",
									"5328",
									"5329",
									"5335",
									"5336",
									"2673" /*TamirS Test Org (dev)*/,
									"1221" /*Relay org*/,
									"27" /*Covercy sandbox org*/,
								]
								const sandboxAndLocalBankIds = ["20", "26"]
								const showAssignmentsOnSandBox =
									isSandboxOrLocalEnv() &&
									(sandboxAndLocalOrgIds.includes(payment.relationships.org?.data.id ?? "") ||
										sandboxAndLocalBankIds.includes(payment.relationships.bank?.data.id ?? "") ||
										isUnitUser) // For MS1 Only, remove when needed.
								const showAssignmentsOnProd = isProdEnv()
								const isShowAssignments =
									isWirePayment(payment) &&
									!!payment.relationships.approvals &&
									!isRedirectedFromAssignments &&
									(showAssignmentsOnSandBox || showAssignmentsOnProd) // For MS1 Only, remove when needed.
								if (!disableDrawer && !isShowAssignments) {
									setSelectedPaymentId(payment.id)
									toggleDrawer(true)
									return
								}
								navigate(
									isShowAssignments
										? `/payments/${payment.id}/assignments#${paymentTabQueryStateName}=${PaymentTabConstants.FullWireDetails}`
										: `/payments/${payment.id}`
								)
							}}
							selectedPaymentId={selectedPaymentId}
							refresh={refresh}
							includedColumns={includedColumns}
						/>
					))}
				</DataTableBody>
			</DataTable>
			<PagingNavBar
				hasResults={hasResults}
				hasPrev={hasPrev}
				hasNext={hasNext}
				prev={prev}
				next={next}
				isShow={isUsingPaging}
				meta={meta}
			/>

			{!disableDrawer && (
				<Drawer
					open={isDrawerOpen}
					onClose={() => {
						toggleDrawer(false)
						setSelectedPaymentId("")
					}}
					direction="right"
					className="check-deposits-side-drawer"
					size={"50%"}
					zIndex={33}
					blockBody={true}
					linkToCopy={`${window.location.origin}/payments/${selectedPaymentId}`}
				>
					{isDrawerOpen && (
						<PaymentComponent
							refresh={refresh}
							paymentId={selectedPaymentId}
							accessToken={accessToken}
							useDrawer={true}
						/>
					)}
				</Drawer>
			)}
		</div>
	)
}
